import { useVirtualizer } from "@tanstack/react-virtual";
import { RefCallback, RefObject, useEffect, useState } from "react";

import type { ApiSchemas } from "@/shared/api/schema";

import { SearchDialogEmpty } from "./search-dialog-empty";
import { SearchDialogLoading } from "./search-dialog-loading";
import { SearchResultItem } from "./search-result-item";
import { SearchResultListSkeleton } from "./search-result-item-skeleton";

type SearchResult = NonNullable<
  ApiSchemas["SearchResultsResponse"]["results"]
>[number];

type SearchDialogContentProps = {
  query: string;
  debouncedQuery: string;
  isPending: boolean;
  results: SearchResult[];
  isFetchingNextPage: boolean;
  selectedIndex: number;
  cursorRef: RefCallback<HTMLDivElement>;
  onSelect: (result: SearchResult) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  savedScrollOffset?: number;
};

function SearchResultsList({
  results,
  isFetchingNextPage,
  selectedIndex,
  cursorRef,
  onSelect,
  scrollContainerRef,
  savedScrollOffset = 0,
}: {
  results: SearchResult[];
  isFetchingNextPage: boolean;
  selectedIndex: number;
  cursorRef: RefCallback<HTMLDivElement>;
  onSelect: (result: SearchResult) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  savedScrollOffset?: number;
}) {
  const [isReady, setIsReady] = useState(false);

  // Wait for scroll container to be ready
  useEffect(() => {
    if (scrollContainerRef.current) {
      const timer = requestAnimationFrame(() => {
        setIsReady(true);
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [scrollContainerRef]);

  // Restore scroll position after mounting
  useEffect(() => {
    if (isReady && savedScrollOffset > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollOffset;
    }
  }, [isReady, savedScrollOffset, scrollContainerRef]);

  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => (isReady ? scrollContainerRef.current : null),
    estimateSize: () => 60,
    overscan: 5,
    initialOffset: savedScrollOffset,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const itemHeight = 60;
  const totalHeight = results.length * itemHeight;

  // Use virtual items if available, otherwise create fallback items
  const itemsToRender =
    virtualItems.length > 0
      ? virtualItems
      : results.map((_, i) => ({
          index: i,
          start: i * itemHeight,
          size: itemHeight,
        }));

  return (
    <div className="p-2">
      <div
        style={{
          height: virtualizer.getTotalSize() || totalHeight,
          width: "100%",
          position: "relative",
        }}
      >
        {itemsToRender.map((row) => {
          const result = results[row.index];
          return (
            <div
              key={`${result.type}-${result.id}`}
              ref={virtualizer.measureElement}
              data-index={row.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${row.start}px)`,
              }}
            >
              <SearchResultItem
                result={result}
                index={row.index}
                isSelected={selectedIndex === row.index}
                onSelect={onSelect}
              />
            </div>
          );
        })}
      </div>
      {/* Cursor for infinite scroll */}
      {cursorRef && <div ref={cursorRef} className="h-2" />}
      {/* Loading indicator for next page */}
      {isFetchingNextPage && <SearchResultListSkeleton count={3} />}
    </div>
  );
}

export function SearchDialogContent({
  query,
  debouncedQuery,
  isPending,
  results,
  isFetchingNextPage,
  selectedIndex,
  cursorRef,
  onSelect,
  scrollContainerRef,
  savedScrollOffset = 0,
}: SearchDialogContentProps) {
  if (!debouncedQuery) {
    return <SearchDialogEmpty variant="empty" />;
  }

  if (isPending || query !== debouncedQuery) {
    return <SearchDialogLoading />;
  }

  if (results.length === 0) {
    return <SearchDialogEmpty variant="no-results" />;
  }

  return (
    <SearchResultsList
      results={results}
      isFetchingNextPage={isFetchingNextPage}
      selectedIndex={selectedIndex}
      cursorRef={cursorRef}
      onSelect={onSelect}
      scrollContainerRef={scrollContainerRef}
      savedScrollOffset={savedScrollOffset}
    />
  );
}
